<template>
  <FlexibleContent style="height: 100%;">
    <template v-slot:header>
      <div class="flex flex-f-c" style="margin: 1.25rem 0 .75rem;">
        <h2 style="width: 100%; margin: 0;" class="text-overflow-fade">
          <span v-if="!state.editName">{{ route.settings?.name || route.name }}</span>
          <input :value="route.settings?.name" @change="updateName" v-else class="inline" type="text" />
        </h2>
        <AppButton v-if="isOwner && !locked" :icon="state.editName ? 'check' : 'edit'" :color="state.editName ? 'primary' : ''" @click="state.editName = !state.editName" class="no-print" />
        <AppButton @click="shareRoute()" v-if="!isOwner" class="no-print" icon="share" />
        <AppLoader v-if="route.loading" size="small" />
        <AppDropdown v-if="isOwner" @click="minimizePlaces = false">
          <AppButton @click="shareRoute()" icon="share" :disabled="locked">{{ $t('route.share') }}</AppButton>
          <AppButton @click="openPlaceImporter()" icon="upload_file" :disabled="locked">{{ $t('import.import_from_excel') }}</AppButton>
          <AppButton @click="exportCsv()" icon="file_download" :disabled="locked">{{ $t('export.export_to_excel') }}</AppButton>
          <AppButton @click="printRoute()" icon="print" :disabled="locked">{{ $t('route.print') }}</AppButton>
          <AppButton class="PLUS" @click="goLive()" icon="play_arrow" :disabled="locked">{{ $t('live.go_live') }}</AppButton>
          <AppButton @click="state.confirmDelete = true" icon="delete" class="color-error">{{ $t('route.delete') }}</AppButton>
        </AppDropdown>
        <AppButton v-if="false && isOwner && !locked" :disabled="route.saved" @click="save" color="primary" :loading="state.saving" class="no-print" >{{route.saved ? 'Gemt' : 'Gem'}}</AppButton>
      </div>

      <div style="margin:.75rem 0">
        <div class="icon-text small" v-if="route.settings.startDate"> <AppIcon name="calendar_today" /> {{ $filters.localDate(route.settings.startDate) }} </div>
        <div class="icon-text small"> <AppIcon :name="$t('vehicles.icons.' + route.settings.vehicle)" /> {{ $t('vehicles.' + route.settings.vehicle) }} </div>
        <div class="icon-text small"> <AppIcon name="timer" /> {{ route.settings.timeLimit > 0 ? 'Auto.' : $filters.duration(route.settings.breakTime) }} </div>
      </div>

      <div v-if="isOwner && !locked" class="no-print flex" style="margin-bottom: 1rem;">
        <LocationSearch @selected="addLocation" />
        <AppButton style="margin-left:.5rem;" @click="openPlaceImporter()" color="secondary" icon="upload_file" :disabled="locked" v-tooltip="$t('import.import')" />
        <AppButton style="margin-left:.5rem;" @click="openSettings()" class="no-print" icon="tune" color="primary" v-tooltip="$t('route.settings')" />
      </div>
      <div id="locked" v-if="isOwner && locked" class="flex flex-_-c">
        <AppIcon name="lock"/> <div style="margin-left:.5rem">Denne rute er låst, fordi antallet af tilladte stop er overskredet</div>
      </div>
      <div v-if="locked && isOwner && route.places.length >= plan.permissions.LIMIT_ROUTE_PLACES" id="upgrade" class="card flex flex-sb-c no-print">
        <span>Brug for mere end {{plan.permissions.LIMIT_ROUTE_PLACES}} stop? </span>
        <AppButton @click="$router.push('/account')" color="primary">Ja tak!</AppButton>
      </div>
    </template>

    <RoutePlaces :route="route" :places="route.places" :liveState="false" :locked="locked" style="margin-bottom: 1rem;" />

    <template v-slot:footer>
      <RouteStats :route="route" />
      <div v-if="!locked && isOwner && route.places.length >= plan.permissions.LIMIT_ROUTE_PLACES && plan.id == 'FREE'" id="upgrade" class="card flex flex-sb-c no-print">
        <span>Brug for mere end {{plan.permissions.LIMIT_ROUTE_PLACES}} stop? </span>
        <AppButton @click="$router.push('/account')" color="primary">Ja tak!</AppButton>
      </div>
    </template>
  </FlexibleContent>
  <Confirm v-model="state.confirmDelete" @confirm="remove()" @cancel="state.confirmDelete = false" :desc="$t('route.confirm_delete_desc', {name: route.settings.name})" />

</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType, reactive } from 'vue'

import AppDropdown from '@/components/atoms/AppDropdown.vue'
import FlexibleContent from '@/components/layout/FlexibleContent.vue'

import LocationSearch from '@/components/route/LocationSearch.vue'
import RoutePlaces from '@/components/route/RoutePlaces.vue'
import RouteStats from '@/components/route/RouteStats.vue'
import Confirm from '@/components/modals/Confirm.vue'

import { store } from '@/store'
import { GA } from '@/api/analytics'

import { Route, PlaceModel, Location } from '@/api/tripply/models'

export default defineComponent({
  name: 'Route',
  props: {
    route: {
      type: Object as PropType<Route>,
      required: true
    }
  },
  components: { AppDropdown, FlexibleContent, LocationSearch, RoutePlaces, RouteStats, Confirm },
  setup(props) {
    const state = reactive({
      editName: false,
      saving: false,
      settings: false,
      confirmDelete: false
    })
    const newName = ref('')
    const minimizePlaces = ref(false)

    const user = store.user.get(state => state.user)
    const plan = computed(() => store.user.getPlan())
    const isOwner = computed(() => store.user.isOwner(props.route.id))

    const showAssignDriver = ref(false)

    const locked = computed(() => isOwner.value && store.route.placeLimitExceeded(props.route.id))

    const addLocation = async (location: Location) => {   
      const place = new PlaceModel(location)   
      store.route.addPlace(props.route.id, place)
      GA('add_place', { method: 'search', number: 1 })   
    }

    async function save() {
      state.saving = true
      await store.route.saveRoute(props.route.id)
      state.saving = false
    }

    function updateName(e: any) {
      store.route.updateSettings(props.route.id, {name: e.target.value as string})
    }

    function goLive() {
      store.route.goLive(props.route.id)
    }

    function remove() {
      store.route.deleteRoute(props.route.id)
    }

    function exportCsv() {
      store.route.exportRoute(props.route.id)
    }

    function printRoute() {
      window.print()
    }

    function openSettings() {
      store.ui.mutate(state => state.route.settingsDrawer = !state.route.settingsDrawer)
      // store.modal.showModal('RouteSettings', {route: props.route})
    }
    function openPlaceImporter() {
      store.modal.showModal('PlaceImporter', {routeId: props.route.id})
    }
    function shareRoute() {
      store.route.share(props.route.id)
    }

    return { store, state, user, plan, save, locked, isOwner, updateName, openPlaceImporter, shareRoute, addLocation, remove, newName, openSettings, showAssignDriver, exportCsv, goLive, printRoute, minimizePlaces }
  },
})
</script>

<style lang="scss" scoped>
#upgrade {
  background: var(--color-primary);
  color: var(--color-on-primary);
  margin-bottom: 1rem;
  padding: 1rem;
  border-radius: 1rem;
}

#locked {
  background: var(--color-error);
  color: white;
  border-radius: 1rem;
  padding: .5rem 1rem;
  font-weight: 500;
  margin-bottom: .5rem;
}

</style>